import { get, post } from '../utils/api/api'
import { productsApi } from '../utils/api/productsApi'
import { apm } from '@elastic/apm-rum'
import { saveAuth, saveToken } from './user'
import { recordMetricEvent, METRIC_EVENTS } from './metrics'
import { getIsDebug } from '../utils/scene/debug/getIsDebug'
import { isArray } from 'lodash'

export function getShopItems() {
  return get(
    `/shop/items`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить список предметов магазина' },
  )
}

export function addFriend(uid) {
  return post(
    `/user/favorite`,
    { uid },
    { apmCustomErrorMessage: 'Не удалось добавить пользователя в избранное по uid' },
  )
}

export function convertToCoins() {
  return post('/gacha/convert-bonuses')
}

export function getFriend(uid) {
  return get(
    `/user/view/?uid=${uid}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить друзей пользователя' },
  )
}

export function getUserData(uid) {
  return get(
    `/user/view/?uid=${uid}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить информацию пользователя' },
  )
}

export function getWishUserData(uid) {
  return get(
    `/user/view/?uid=${uid}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить открытки пользователя' },
  )
}

export function saveServiceData(data) {
  return post(`/user/service-data-save`, data, {
    apmCustomErrorMessage: 'Не удалось сохранить сервисные данные',
  })
}

export function saveServiceModal(data) {
  return post(`/user/service-data-modal-save`, data, {
    apmCustomErrorMessage: 'Не удалось сохранить сервисные данных о показе модального окна',
  })
}

export function getServiceData() {
  return get(
    `/user/service-data-get`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить сервисные данные' },
  )
}

export function getFavorites({ page, perPage }) {
  return get(
    `/user/favorites/?page=${page}&per-page=${perPage}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить список избранного' },
  )
}

export function setLike({ uid, type }) {
  return post(
    '/user/like',
    { uid, type },
    { apmCustomErrorMessage: 'Ошибка при попытке поставить лайк пользователю по его uid' },
  )
}

export function setDislike({ uid, type }) {
  return post(
    '/user/dislike',
    { uid, type },
    { apmCustomErrorMessage: 'Ошибка при попытке поставить дизлайк пользователю по его uid' },
  )
}

export function getCurrentQuiz() {
  return get(
    '/game/current-quiz',
    {},
    { apmCustomErrorMessage: 'Не удалось получить текущий квиз' },
  )
}

export function buyQuiz() {
  return post('/game/buy-quiz', {}, { apmCustomErrorMessage: 'Не удалось купить квиз' })
}

export function submitCurrentQuizResult(answers) {
  return post(
    '/game/submit-quiz-result',
    { answers },
    { apmCustomErrorMessage: 'Не удалось отправить результат квиза' },
  ).then(saveAuth)
}

export function buyItem(id) {
  return post(
    `/shop/buy/?id=${id}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить новый профиль с купленным предметом по айди' },
  ).then(saveAuth)
}

export function equipItem(id) {
  return post(
    `/shop/equip/?id=${id}`,
    {},
    {
      apmCustomErrorMessage: 'Не удалось получить новый профиль с экипированным предметом по айди',
    },
  ).then(saveAuth)
}

export function getRating({ params = {} } = {}) {
  return post(
    `/game/rating`,
    { params },
    { apmCustomErrorMessage: 'Не удалось получить текущий рейтинг' },
  )
}

export function getWeekRating({ updatedAt = undefined }) {
  return post(
    `/game/week-rating${updatedAt ? `?RatingSearch[season_id]=${updatedAt}` : ''}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить текущий недельный рейтинг' },
  )
}

export function getUserRating({ params = {} } = {}) {
  return post(
    '/game/user-rating',
    { params },
    { apmCustomErrorMessage: 'Не удалось получить текущий рейтинг пользователя' },
  )
}

export function getUserWeekRating({ params = {} } = {}) {
  return post(
    '/game/user-week-rating',
    { params },
    { apmCustomErrorMessage: 'Не удалось получить текущий рейтинг пользователя' },
  )
}

export function getQuests() {
  return get('/game/quests', {}, { apmCustomErrorMessage: 'Не удалось получить список квестов' })
}

/**
 * Отмечает квест как завершённый.
 *
 * @param {CompleteQuestData} params Данные для запроса.
 * @returns {Promise<any>} Промис с результатом запроса.
 */
export function completeQuest({ id, word }) {
  return post(
    `/game/complete-quest/?id=${id}`,
    { segment: word },
    { apmCustomErrorMessage: 'Не удалось завершить квест по айди' },
  ).then(saveAuth)
}

export function collectQuest(id) {
  return post(
    `/game/collect-quest/?id=${id}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить награду квеста по айди' },
  ).then(saveAuth)
}

export function gameStart(type) {
  return post(
    `/game/start`,
    { type },
    { apmCustomErrorMessage: `Не удалось начать игру ${type}` },
  ).then(saveAuth)
}

const buildGameRatingTypeQuery = (type) => {
  if (typeof type !== 'string') {
    // TODO: удалить после проверки что ошибки перестанут приходить на кибану бэка
    apm.captureError("getGameRating: 'type' must be string", {
      labels: {
        'error.type': 'TypeConversionError',
        'error.value': JSON.stringify(type),
      },
    })

    return type?.apiName ?? type?.name ?? type?.type ?? null
  }

  return type
}

export function getGameRating({ type, updatedAt = undefined }) {
  const typeParam = buildGameRatingTypeQuery(type)

  return get(
    `/game/game-rating?${typeParam ? `type=${encodeURIComponent(typeParam)}` : ''}${
      updatedAt ? `&GameRatingSearch[season_id]=${updatedAt}` : ''
    }`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить текущий рейтинг игры' },
  )
}

export function getUserGameRating({ type, updatedAt = undefined }) {
  const typeParam = buildGameRatingTypeQuery(type)

  // return get(
  //   `/game/user-game-rating?${typeParam ? `type=${encodeURIComponent(typeParam)}` : ''}${
  //     updatedAt ? `&UserGameRatingSearch[season_id]=${updatedAt}` : ''
  //   }`,
  //   {},
  //   { apmCustomErrorMessage: 'Не удалось получить текущий рейтинг игры пользователя' }
  // );

  return Promise.resolve()
}

export function getOldWeekRating() {
  return get(
    '/game/old-week-rating',
    {},
    { apmCustomErrorMessage: 'Не удалось получить исторический недельный рейтинг' },
  )
}

export function gameEnd({ is_win, score, money, booster_count, boosters, logs, hash }) {
  const clearedData = Object.entries({
    is_win,
    score,
    money,
    logs,
    hash,
    booster_count,
    ...(boosters ?? {}),
  }).reduce((acc, [key, value]) => {
    return ['number', 'string'].includes(typeof value) ? { ...acc, [key]: value } : acc
  }, {})
  return post(`/game/end`, clearedData, {
    apmCustomErrorMessage: 'Не удалось закончить игру',
  }).then(saveAuth)
}

export function spfGameStart({ name }) {
  const clearedData = Object.entries({
    name,
  }).reduce((acc, [key, value]) => {
    return ['number', 'string'].includes(typeof value) ? { ...acc, [key]: value } : acc
  }, {})
  return post(`/game/start-hidden-object`, clearedData, {
    apmCustomErrorMessage: 'Не удалось начать прохождение локации',
  })
}

export function spfGameEnd({ name }) {
  const clearedData = Object.entries({
    name,
  }).reduce((acc, [key, value]) => {
    return ['number', 'string'].includes(typeof value) ? { ...acc, [key]: value } : acc
  }, {})
  return post(`/game/end-hidden-object`, clearedData, {
    apmCustomErrorMessage: 'Не удалось отправить результат прохождения локации',
  })
}

export function spfGameExit() {
  return post(
    `/game/exit-hidden-object`,
    {},
    { apmCustomErrorMessage: 'Не удалось выйти из локации' },
  )
}

export function getIndex() {
  return get(
    '/data/index',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список статичного контента' },
  )
}

export function buyPrize(id) {
  return post(
    `/shop/buy-prize/?id=${id}`,
    {},
    { apmCustomErrorMessage: 'Не удалось купить приз по айди' },
  ).then(saveAuth)
}

export function getGameSettings() {
  return get(
    '/game/list',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список доступных игр' },
  )
}

export function getGrades() {
  return get(
    '/data/grades',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список грейдов розыгрыша' },
  )
}

export function getParams() {
  return get(
    '/params/index',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список публичных параметров' },
  )
}

export function setActiveRoom(activeRoom) {
  return post(
    '/user/change-room',
    { id: activeRoom },
    { apmCustomErrorMessage: 'Не удалось изменить интерьер комнаты' },
  ).then(saveAuth)
}

export function buyLootBoxById(id) {
  return post('/gacha/open', { id }, { apmCustomErrorMessage: 'Не удалось открыть бокс по айди' })
}

export function getLootBoxesList() {
  return get(
    `/gacha/list`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить список айтемов в боксах' },
  )
}

export function getRareRating() {
  return post(
    '/gacha/event-rating',
    {},
    { apmCustomErrorMessage: 'Не удалось получить текущий рейтинг акционных лутбоксов' },
  )
}

export function getUserRareRating() {
  return post(
    '/gacha/user-event-rating',
    {},
    {
      apmCustomErrorMessage: 'Не удалось получить текущий рейтинг акционных лутбоксов пользователя',
    },
  )
}

export function getBuyProducts() {
  return get(
    '/game/product-beauty-procedures',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список процедур за шоппинг' },
  )
}

export function useBuyProduct(id) {
  return post(
    `/game/product-beauty-procedure/?id=${id}`,
    {},
    { apmCustomErrorMessage: 'Не удалось применить процедуру за шоппинг по айди' },
  )
}

export function productToFavorites(sku) {
  return post(
    `/game/favourite-product/?id=${sku}`,
    {},
    { apmCustomErrorMessage: 'Не удалось добавить продукт в избранное по айди' },
  )
}

export function productToCart(sku) {
  return post(
    `/game/add-product-to-cart/?id=${sku}`,
    {},
    { apmCustomErrorMessage: 'Не удалось добавить продукт в корзину по айди' },
  )
}

export function getFavoritesProducts() {
  return get(`/game/products`, {}, { apmCustomErrorMessage: 'Не удалось получить продукты' })
}

export function getWebCatalogProductsBySku(itemIdsOrParams, { authData }) {
  const params = isArray(itemIdsOrParams)
    ? {
        itemIds: itemIdsOrParams,
        ...authData?.addressParams,
        ...(getIsDebug() && { cityId: '0c5b2444-70a0-4932-980c-b4dc0d3f02b5' }),
      }
    : itemIdsOrParams
  return productsApi.post('/v3/catalog/productblockbysku', params, {
    apmCustomErrorMessage: 'Не удалось получить продукты',
  })
}

export function getWebCatalogProductsBySkuBeautyRoutine(itemIdsOrParams, { authData }) {
  const params = isArray(itemIdsOrParams)
    ? {
        itemIds: itemIdsOrParams,
        ...authData?.addressParams,
        ...(getIsDebug() && { cityId: '0c5b2444-70a0-4932-980c-b4dc0d3f02b5' }),
      }
    : itemIdsOrParams
  return productsApi.post('/v3/catalog/productblockbysku', params, {
    apmCustomErrorMessage: 'Не удалось получить продукты подборки в бьюти рутине',
  })
}

export function returnAttempts(attempts) {
  return post(
    `/game/abort`,
    { revertAttempts: attempts },
    { apmCustomErrorMessage: 'Ошибка при отмене игры' },
  )
}

export function getAccessToken(phone) {
  return post(
    `/user/access-token`,
    { phone },
    { apmCustomErrorMessage: 'Не удалось получить токен доступа' },
  ).then(saveToken)
}

// Учёт показов окна авторизации. Лейблы на /metrics/events запрещены,
// поэтому дискриминатор зашит в имя метрики:
//  - hadToken — сохранённый игровой токен на месте (магазин не выдал свой)
//  - иначе хранилище WebView стёрто
export function reportAuthWindowShown({ hadToken, reason }) {
  if (reason === 'guest_no_token') {
    return recordMetricEvent(METRIC_EVENTS.AUTH_WINDOW_SHOWN_GUEST)
  }
  if (hadToken) {
    return recordMetricEvent(METRIC_EVENTS.AUTH_WINDOW_SHOWN_GAME_TOKEN_STORED)
  }
  return recordMetricEvent(METRIC_EVENTS.AUTH_WINDOW_SHOWN_NO_SIGNALS)
}

export function auth(userData) {
  return post(`/user/auth`, userData, { apmCustomErrorMessage: 'Ошибка авторизации' }).then(
    saveAuth,
  )
}

export function getTgBonuses() {
  return get(
    '/tg/bonuses',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список бонусов клиента' },
  )
}

export function getTgEnergy() {
  return get(
    '/tg/energy',
    {},
    { apmCustomErrorMessage: 'Не удалось получить статистику энергии клиента' },
  )
}

export function getTgMoney() {
  return get(
    '/tg/money',
    {},
    { apmCustomErrorMessage: 'Не удалось получить статистику монет клиента' },
  )
}

export function getTgTickets() {
  return get(
    '/tg/tickets',
    {},
    { apmCustomErrorMessage: 'Не удалось получить статистику купонов клиента' },
  )
}

export function getTgGifts() {
  return get(
    '/tg/gifts',
    {},
    { apmCustomErrorMessage: 'Не удалось получить статистику гифтов и премиума клиента' },
  )
}

export function getModals() {
  return get(
    '/user/modals',
    {},
    { apmCustomErrorMessage: 'Не удалось получить список модальных окон' },
  )
}

export function sendGameProgress(data) {
  return post('/game/save-progress', data, {
    apmCustomErrorMessage: 'Не удалось сохранить прогресс игры',
  })
}

export { getBeautyRoutine, endBeautyRoutine } from './beautyRoutine'

export function sendWish({ uid, wishId, wishTextId, name, elf }) {
  return post(
    '/wish/send',
    { uid, wish_id: wishId, wish_text_id: wishTextId, name, elf },
    { apmCustomErrorMessage: 'Не удалось отправить открытку' },
  )
}

export function getWishes({ page = 1, perPage = 100 } = {}) {
  return get(
    `/wish/list?page=${page}&per-page=${perPage}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить открытки' },
  )
}

export function getWishUserItems(uid) {
  return post(
    '/wish/shop-items',
    { uid },
    { apmCustomErrorMessage: 'Не удалось получить список подарков' },
  )
}

export function buyWishGift({ uid, id }) {
  return post(
    '/wish/gift-shop-item',
    { uid, shopItemId: id },
    { apmCustomErrorMessage: 'Не удалось отправить подарок' },
  )
}

export function setPremiumIcon(iconData) {
  return post('/user/change-icon', iconData, {
    apmCustomErrorMessage: 'Не удалось изменить иконку',
  })
}

export function fromGachaToAttempts() {
  return post('/gacha/convert', {}, { apmCustomErrorMessage: 'Не удалось конвертировать энергию' })
}

export function buyPremium(id) {
  return post('/gacha/premium', { id }, { apmCustomErrorMessage: 'Не удалось купить премиум' })
}

export function getQuizQuests() {
  return get(
    `/game/quiz-quests`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить список квестов нового года' },
  )
}

export function submitQuizQuest(data) {
  return post(`/game/submit-quiz-quest-result`, data, {
    apmCustomErrorMessage: 'Не удалось отправить результаты квиза',
  })
}

export function openCustomModal(id) {
  return post(
    `/user/modal-button/?id=${id}`,
    {},
    { apmCustomErrorMessage: 'Ошибка при нажатии на кнопку модального окна по айди' },
  )
}

export function getIcons() {
  return post(`/data/icons`, {}, { apmCustomErrorMessage: 'Не удалось получить иконки' })
}

export function getIconsByUid(uid) {
  return post(
    `/data/icons/?uid=${uid}`,
    {},
    { apmCustomErrorMessage: 'Не удалось получить иконку по айди' },
  )
}

export function getGuessPriceTodayList() {
  return get(
    '/guess-price/today-list',
    {},
    { apmCustomErrorMessage: 'Не удалось получить продукты' },
  )
}

export function checkGuessPrice({ id, from, to }) {
  return post(
    '/guess-price/guess',
    { id, price_from: from, price_to: to },
    { apmCustomErrorMessage: 'Не удалось проверить цену' },
  )
}

export async function getYearStat() {
  return get(
    '/data/highlights',
    {},
    { apmCustomErrorMessage: 'Не удалость получить годовую статистику пользователя' },
  )
}

export function markDisposableModalAsShown(id) {
  return post(
    `/user/modals-tracking`,
    { id },
    { apmCustomErrorMessage: 'Не удалось отметить модальное окно из админки как просмотренное' },
  )
}

/**
 *
 * @param image - base64 string
 */
export function saveShareImage(image) {
  return post(
    '/user/save-share-image',
    { image },
    { apmCustomErrorMessage: 'Не удалось сохранить картинки шеринга' },
  )
}

export function getShopPrizes() {
  return get('/shop/prizes')
}
